// Patterns that indicate a raw/technical backend error that should never be
// shown to end users (JS runtime errors, stack traces, SQL/driver noise, etc.).
const TECHNICAL_ERROR_PATTERN =
  /\b(is not defined|undefined|null|cannot read|reference\s?error|type\s?error|stack|sql|exception|syntax|econn|timeout|500|502|503|504|nan|\[object)/i;

const PAYSTUB_LIMIT_PATTERN =
  /can\s*not\s+upload\s+more\s+than\s+8\s+pay\s*stubs/i;

// Only the exact sentence produced after a paystub split is accepted. The
// count is read from that sentence and the user-facing copy is rebuilt, so a
// backend message cannot smuggle extra text into the toast.
const SPLIT_FILE_PAYSTUB_COUNT_PATTERN =
  /^This file contains ([2-9]|[1-9]\d) paystubs\. Can not upload more than 8 paystubs$/i;

const RAW_PAYSTUB_SPLIT_PATTERN = /unable to split/i;
const PAYSTUB_WORD_PATTERN = /pay\s?stub/i;

export const MOVE_NOTIFICATION_DURATION_MS = 2000;
export const MOVE_FAILURE_NOTIFICATION_DURATION_MS = 3000;
// CustomNotification closes whichever snackbar is already open as soon as
// another notification is enqueued. This extra gap lets that snackbar finish
// leaving before the next move notification is enqueued, so the previous
// message stays up for its full auto-hide duration.
export const MOVE_NOTIFICATION_HANDOFF_MS = 600;

const MAX_FAILURE_REASON_LENGTH = 240;

export const PAYSTUB_LIMIT_NOTIFICATION =
  "Cannot upload more than 8 paystubs in Employment Income.";

export type MoveResultNotification = {
  message: string;
  type: "success" | "info" | "error";
  autoHideDuration: number;
};

export type MoveResultNotificationSequence = {
  notifications: MoveResultNotification[];
  persistentMessage: string | null;
};

export const isHumanReadableMessage = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) return false;
  if (TECHNICAL_ERROR_PATTERN.test(trimmed)) return false;
  // Reject single-token / code-ish strings (no spaces, looks like an identifier).
  if (!/\s/.test(trimmed) && /[._(){}]/.test(trimmed)) return false;
  return true;
};

export const toErrorMessage = (error: any, fallback: string) => {
  const message = error?.message;
  if (typeof message === "string" && isHumanReadableMessage(sanitizeReason(message))) {
    return sanitizeReason(message);
  }
  return fallback;
};

export const isRawPaystubSplitFailure = (reason: string) =>
  RAW_PAYSTUB_SPLIT_PATTERN.test(reason) && PAYSTUB_WORD_PATTERN.test(reason);

const toCount = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
};

const documentWord = (count: number) => (count === 1 ? "document" : "documents");

export const remainingNotificationHoldMs = (visibleUntil: number, now: number) =>
  Math.max(0, visibleUntil - now);

export const nextNotificationVisibleUntil = (now: number, autoHideDuration: number) =>
  now + autoHideDuration + MOVE_NOTIFICATION_HANDOFF_MS;

export const sanitizeReason = (reason: string) =>
  reason
    .replace(/<[^>]*>/g, "")
    .replace(/[<>]/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .trim()
    .slice(0, MAX_FAILURE_REASON_LENGTH);

const isPaystubLimitReason = (reason: string) => PAYSTUB_LIMIT_PATTERN.test(reason);

const splitFilePaystubCount = (reason: string) => {
  const match = reason.match(SPLIT_FILE_PAYSTUB_COUNT_PATTERN);
  if (!match) return null;
  const count = Number(match[1]);
  if (!Number.isInteger(count) || count < 2 || count > 99) return null;
  return count;
};

const resolveFailureReason = (reasons: string[], failedCount: number) => {
  const readable = reasons
    .map((reason) => sanitizeReason(reason))
    .filter((reason) => isHumanReadableMessage(reason));

  const splitCount = readable
    .map(splitFilePaystubCount)
    .find((count) => count != null);
  if (splitCount != null) {
    return `This file contains ${splitCount} paystubs. ${PAYSTUB_LIMIT_NOTIFICATION}`;
  }
  if (readable.some(isPaystubLimitReason)) {
    return PAYSTUB_LIMIT_NOTIFICATION;
  }
  if (readable.some(isRawPaystubSplitFailure)) {
    return failedCount === 1
      ? "The selected document could not be processed as a pay stub."
      : "The selected documents could not be processed as pay stubs.";
  }
  return readable[0] || "Please try again.";
};

export const buildMoveResultNotificationSequence = ({
  succeeded,
  failed,
  failureReasons = [],
}: {
  succeeded: number;
  failed: number;
  failureReasons?: string[];
}): MoveResultNotificationSequence => {
  const succeededCount = toCount(succeeded);
  const failedCount = toCount(failed);
  const total = succeededCount + failedCount;
  if (total === 0) {
    return { notifications: [], persistentMessage: null };
  }

  const moveNotification: MoveResultNotification = {
    message: `Move started for ${succeededCount} of ${total} ${documentWord(total)}.`,
    type: failedCount > 0 ? "info" : "success",
    autoHideDuration: MOVE_NOTIFICATION_DURATION_MS,
  };

  if (failedCount === 0) {
    return { notifications: [moveNotification], persistentMessage: null };
  }

  const failureMessage = `${failedCount} ${documentWord(failedCount)} failed to move. ${resolveFailureReason(failureReasons, failedCount)}`;
  const failureNotification: MoveResultNotification = {
    message: failureMessage,
    type: "error",
    autoHideDuration: MOVE_FAILURE_NOTIFICATION_DURATION_MS,
  };

  return {
    notifications: [moveNotification, failureNotification],
    persistentMessage: failureMessage,
  };
};
